import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import InnerLayout from '../../components/layouts/innerLayout';
import InputField from '../../components/forms/InputField';
import { Form, Formik } from 'formik';
import { formatDate, formatString, getImageBase64FromUrl } from '../../components/helper';
import jsPDF from "jspdf";
import "jspdf-autotable";
import { errorToast } from '../../components/toast';
import { useDispatch } from 'react-redux';
import { accountDetailsAction } from '../../redux/actions/authAction';
import { InvoiceDownload, InvoicePrinter } from '../../components/print/printLandscapeTemplate';
import ThermalInvoice from '../../components/print/thermalPrint';

const InvoiceView = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const invoiceData = location.state?.invoiceData || {};
    const invoicedProducts = invoiceData?.invoicedProducts || [];
    const [accountData, setAccountData] = useState(null);
    const [loading, setLoading] = useState(false);
    const isMobile = /Mobi|Android/i.test(navigator.userAgent);


    useEffect(() => {
        fetchAccount()
    }, [])

    const fetchAccount = () => {
        setLoading(true)
        dispatch(accountDetailsAction({}, onSuccess, onError))
    }

    const onSuccess = (data) => {
        setAccountData(data.data.data);
    }

    const onError = (data) => {
        const errorMsg = data.data.message || "Unable to fetch try later";
        errorToast(errorMsg)
        setLoading(false)
    }

    const generateBillForPrint = async (invoiceData) => {
        const doc = new jsPDF();

        let logoBase64 = null;
        if (accountData?.logo) {
            try {
                logoBase64 = await getImageBase64FromUrl(accountData.logo);
            } catch (error) {
                console.error("Error fetching logo:", error);
            }
        }

        // Add Logo
        if (logoBase64) {
            doc.addImage(logoBase64, "JPEG", 10, 10, 20, 20);
        }

        // Add Company Details
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text(accountData?.name?.toUpperCase() || "Company Name", logoBase64 ? 35 : 10, 15);

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(`Email: ${accountData?.email || "N/A"}, Mobile: ${accountData?.mobile || "N/A"}`, logoBase64 ? 35 : 10, 20);
        doc.text(`Address: ${accountData?.address || "N/A"}, ${accountData?.city || "N/A"}, ${accountData?.pincode || "N/A"}, ${accountData?.state || "N/A"}`, logoBase64 ? 35 : 10, 25);

        // Add Invoice and Customer Details
        doc.setFontSize(11);

        // Left Section: Invoice Details
        doc.text(`Invoice Number: ${invoiceData?.code || "N/A"}`, 10, 40);
        doc.text(`Invoice Date: ${formatDate(invoiceData?.createdAt) || "N/A"}`, 10, 45);
        if (invoiceData?.gstNumber) {
            doc.text(`GSTIN: ${invoiceData?.gstNumber || "N/A"}`, 10, 50);
        }
        if (accountData?.fssaiNumber) {
            doc.text(`FSSAI: ${accountData?.fssaiNumber || "N/A"}`, 10, invoiceData?.gstNumber ? 55 : 50);
        }

        // Right Section: Customer Details
        const pageWidth = doc.internal.pageSize.getWidth();
        const rightAlignX = pageWidth - 10;
        doc.text(`Customer Name: ${invoiceData?.customerName || "N/A"}`, rightAlignX, 40, { align: "right" });
        doc.text(`Customer Mobile: ${invoiceData?.customerPhone || "N/A"}`, rightAlignX, 45, { align: "right" });

        // Table Headers
        const tableHeaders = [["Item Name", "HSN Code", "Price", "Discount %", "GST %", "Quantity", "Total"]];

        // Table Rows
        const tableRows = invoiceData?.invoicedProducts.map((product) => [
            product.product.name,
            product.product.hsnCode,
            product.price,
            product.discount,
            product.tax,
            product.quantity,
            product.totalPrice,
        ]);

        // Render Product Table
        doc.autoTable({
            head: tableHeaders,
            body: tableRows,
            startY: invoiceData?.gstNumber ? 60 : 55,
            theme: "striped",
            styles: { cellPadding: 3, fontSize: 10 },
        });

        // Calculate Y Position after the Table
        const tableEndY = doc.autoTable.previous.finalY + 10;

        // Summary Table
        const summaryData = [
            ["Total Items", "", "", "", "", "", invoiceData?.invoicedProducts.length],
            ["Total Tax", "", "", "", "", "", (
                parseFloat(invoiceData?.totalCgst || 0) +
                parseFloat(invoiceData?.totalSgst || 0) +
                parseFloat(invoiceData?.totalIgst || 0)
            ).toFixed(2)],
            ["Subtotal", "", "", "", "", "", parseFloat(invoiceData?.subTotal || 0).toFixed(2)],
            ["Grand Total", "", "", "", "", "", parseFloat(invoiceData?.grandTotal || 0).toFixed(2)],
        ];

        doc.autoTable({
            startY: tableEndY,
            body: summaryData,
            theme: "striped",
            styles: { fontSize: 10, cellPadding: 2 },
            columnStyles: {
                0: { fontStyle: "bold" }, // First column bold for labels
                6: { halign: "right" },  // Align values to the last column
            },
        });

        // Generate PDF as Blob
        const pdfBlob = doc.output("blob");

        if (isMobile) {
            const blobUrl = URL.createObjectURL(pdfBlob);
            // Wrap in a click event listener
            const link = document.createElement("a");
            link.href = blobUrl;
            link.target = "_blank";
            link.click();
        } else {

            // Create an iframe to render the PDF
            const iframe = document.createElement("iframe");
            iframe.style.display = "none";
            document.body.appendChild(iframe);

            // Load the PDF into the iframe
            iframe.src = URL.createObjectURL(pdfBlob);

            // Listener to clean up the iframe after the print dialog is handled
            const cleanupIframe = () => {
                document.body.removeChild(iframe);
                window.removeEventListener("focus", cleanupIframe);
            };

            // Attach the listener to the `focus` event
            window.addEventListener("focus", cleanupIframe);

            // Trigger print when iframe loads
            iframe.onload = () => {
                iframe.contentWindow.print();
            };
        }
    };

    const generateBillForDownload = async (invoiceData) => {
        const doc = new jsPDF();

        let logoBase64 = null;
        if (accountData?.logo) {
            try {
                logoBase64 = await getImageBase64FromUrl(accountData.logo);
            } catch (error) {
                console.error("Error fetching logo:", error);
            }
        }

        // Add Logo
        if (logoBase64) {
            doc.addImage(logoBase64, "JPEG", 10, 10, 20, 20);
        }

        // Add Company Details
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text(accountData?.name?.toUpperCase() || "Company Name", logoBase64 ? 35 : 10, 15);

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(`Email: ${accountData?.email || "N/A"}, Mobile: ${accountData?.mobile || "N/A"}`, logoBase64 ? 35 : 10, 20);
        doc.text(`Address: ${accountData?.address || "N/A"}, ${accountData?.city || "N/A"}, ${accountData?.pincode || "N/A"}, ${accountData?.state || "N/A"}`, logoBase64 ? 35 : 10, 25);

        // Add Invoice and Customer Details
        doc.setFontSize(11);

        // Left Section: Invoice Details
        doc.text(`Invoice Number: ${invoiceData?.code || "N/A"}`, 10, 40);
        doc.text(`Invoice Date: ${formatDate(invoiceData?.createdAt) || "N/A"}`, 10, 45);
        if (invoiceData?.gstNumber) {
            doc.text(`GSTIN: ${invoiceData?.gstNumber || "N/A"}`, 10, 50);
        }
        if (accountData?.fssaiNumber) {
            doc.text(`FSSAI: ${accountData?.fssaiNumber || "N/A"}`, 10, invoiceData?.gstNumber ? 55 : 50);
        }

        // Right Section: Customer Details
        const pageWidth = doc.internal.pageSize.getWidth();
        const rightAlignX = pageWidth - 10;
        doc.text(`Customer Name: ${invoiceData?.customerName || "N/A"}`, rightAlignX, 40, { align: "right" });
        doc.text(`Customer Mobile: ${invoiceData?.customerPhone || "N/A"}`, rightAlignX, 45, { align: "right" });

        // Table Headers
        const tableHeaders = [["Item Name", "HSN Code", "Price", "Discount %", "GST %", "Quantity", "Total"]];

        // Table Rows
        const tableRows = invoiceData?.invoicedProducts.map((product) => [
            product.product.name,
            product.product.hsnCode,
            product.price,
            product.discount,
            product.tax,
            product.quantity,
            product.totalPrice,
        ]);

        // Render Product Table
        doc.autoTable({
            head: tableHeaders,
            body: tableRows,
            startY: invoiceData?.gstNumber ? 60 : 55,
            theme: "striped",
            styles: { cellPadding: 3, fontSize: 10 },
        });

        // Calculate Y Position after the Table
        const tableEndY = doc.autoTable.previous.finalY + 10;

        // Summary Table
        const summaryData = [
            ["Total Items", "", "", "", "", "", invoiceData?.invoicedProducts.length],
            ["Total Tax", "", "", "", "", "", (
                parseFloat(invoiceData?.totalCgst || 0) +
                parseFloat(invoiceData?.totalSgst || 0) +
                parseFloat(invoiceData?.totalIgst || 0)
            ).toFixed(2)],
            ["Subtotal", "", "", "", "", "", parseFloat(invoiceData?.subTotal || 0).toFixed(2)],
            ["Grand Total", "", "", "", "", "", parseFloat(invoiceData?.grandTotal || 0).toFixed(2)],
        ];

        doc.autoTable({
            startY: tableEndY,
            body: summaryData,
            theme: "striped",
            styles: { fontSize: 10, cellPadding: 2 },
            columnStyles: {
                0: { fontStyle: "bold" }, // First column bold for labels
                6: { halign: "right" },  // Align values to the last column
            },
        });

        doc.save(`${invoiceData?.customerName}_invoice.pdf`);
    };

    return (
        <InnerLayout module={`Invoice - ${invoiceData?.code}`}>
            <Formik
                initialValues={{
                    customerPhone: '',
                    customerName: '',
                    payment_method: '',
                    products: invoicedProducts,
                }}
                enableReinitialize
                onSubmit={() => window.print()}
            >
                {({ setFieldValue, values }) => (
                    <Form>
                        <div className="p-2 bg-gray-100" style={{ height: '80vh' }}>
                            <div className="bg-white shadow rounded p-4 overflow-auto" style={{ height: "85%" }}>

                                {/* Customer Details */}
                                <div className="flex flex-wrap gap-4">
                                    <div>
                                        <InputField
                                            value={invoiceData.code || ''}
                                            label="Invoice Number"
                                            name="customerPhone"
                                            type="text"
                                            readOnly
                                            style={{ width: "100%", maxWidth: "20rem" }}
                                        />
                                    </div>
                                    <div>
                                        <InputField
                                            value={formatDate(invoiceData.createdAt) || ''}
                                            label="Invoiced Date"
                                            name="customerPhone"
                                            type="text"
                                            readOnly
                                            style={{ width: "100%", maxWidth: "20rem" }}
                                        />
                                    </div>
                                    <div>
                                        <InputField
                                            value={invoiceData.customerPhone || ''}
                                            label="Customer Phone"
                                            name="customerPhone"
                                            type="text"
                                            placeholder="Enter customer phone"
                                            readOnly
                                            style={{ width: "100%", maxWidth: "20rem" }}
                                        />
                                    </div>
                                    <div>
                                        <InputField
                                            value={invoiceData.customerName || ''}
                                            label="Customer Name"
                                            name="customerName"
                                            type="text"
                                            placeholder="Enter customer name"
                                            readOnly
                                            style={{ width: "100%", maxWidth: "20rem" }}
                                        />
                                    </div>
                                    <div>
                                        <InputField
                                            value={formatString(invoiceData.payment_method) || ''}
                                            label="Payment Method"
                                            name="payment_method"
                                            type="text"
                                            placeholder="Payment Method"
                                            readOnly
                                            style={{ width: "100%", maxWidth: "20rem" }}
                                        />
                                    </div>
                                </div>

                                {/* Product Table */}
                                <div className="overflow-x-auto h-full" style={{ maxHeight: '30rem' }}>
                                    <table className="w-full border-collapse border text-sm">
                                        <thead className="sticky top-0 z-10">
                                            <tr className="bg-gray-100">
                                                <th className="border px-2 py-1">S No.</th>
                                                <th className="border px-2 py-1">Product Name</th>
                                                <th className="border px-2 py-1">Quantity</th>
                                                <th className="border px-2 py-1">Hsncode</th>
                                                <th className="border px-2 py-1">Price</th>
                                                <th className="border px-2 py-1">Discount (%)</th>
                                                <th className="border px-2 py-1">Discounted Price</th>
                                                {invoiceData?.gstNumber &&
                                                    <>
                                                        <th className="border px-2 py-1">Tax (%)</th>
                                                        <th className="border px-2 py-1">CGST</th>
                                                        <th className="border px-2 py-1">SGST</th>
                                                        <th className="border px-2 py-1">IGST</th>
                                                        <th className="border px-2 py-1">Total Tax</th>
                                                    </>
                                                }
                                                <th className="border px-2 py-1">Unit Price</th>
                                                <th className="border px-2 py-1">Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {values.products.map((product, index) => (
                                                <tr key={index}>
                                                    <td className="border px-2 py-1 text-center">
                                                        <InputField
                                                            value={index + 1}
                                                            name={`products[${index}].serialNo`}
                                                            type="text"
                                                            style={{ width: "100%", minWidth: "3rem", maxWidth: "3rem", marginTop: '1rem' }}
                                                            readOnly
                                                        />
                                                    </td>
                                                    <td className="border px-2 py-1">
                                                        <InputField
                                                            value={product.product.name}
                                                            name={`products[${index}].name`}
                                                            type="text"
                                                            style={{ width: "100%", minWidth: "8rem", maxWidth: "10rem", marginTop: '1rem' }}
                                                            readOnly
                                                        />
                                                    </td>
                                                    <td className="border px-2 py-1">
                                                        <InputField
                                                            value={product.quantity}
                                                            name={`products[${index}].quantity`}
                                                            type="number"
                                                            style={{ width: "100%", minWidth: "6rem", maxWidth: "7rem", marginTop: '1rem' }}
                                                            readOnly
                                                        />
                                                    </td>
                                                    <td className="border px-2 py-1">
                                                        <InputField
                                                            value={product.product.hsnCode}
                                                            name={`products[${index}].hsnCode`}
                                                            type="text"
                                                            style={{ width: "100%", minWidth: "6rem", maxWidth: "7rem", marginTop: '1rem' }}
                                                            readOnly
                                                        />
                                                    </td>
                                                    <td className="border px-2 py-1">
                                                        <InputField
                                                            value={product.price}
                                                            name={`products[${index}].price`}
                                                            type="number"
                                                            style={{ width: "100%", minWidth: "6rem", maxWidth: "7rem", marginTop: '1rem' }}
                                                            readOnly
                                                        />
                                                    </td>
                                                    <td className="border px-2 py-1">
                                                        <InputField
                                                            value={product.discount}
                                                            name={`products[${index}].discount`}
                                                            type="number"
                                                            style={{ width: "100%", minWidth: "6rem", maxWidth: "7rem", marginTop: '1rem' }}
                                                            readOnly
                                                        />
                                                    </td>
                                                    <td className="border px-2 py-1">
                                                        <InputField
                                                            value={product.discountedPrice}
                                                            name={`products[${index}].discountedPrice`}
                                                            type="number"
                                                            style={{ width: "100%", minWidth: "6rem", maxWidth: "7rem", marginTop: '1rem' }}
                                                            readOnly
                                                        />
                                                    </td>
                                                    {invoiceData?.gstNumber &&
                                                        <>
                                                            <td className="border px-2 py-1">
                                                                <InputField
                                                                    value={product.tax}
                                                                    name={`products[${index}].tax`}
                                                                    type="number"
                                                                    style={{ width: "100%", minWidth: "6rem", maxWidth: "7rem", marginTop: '1rem' }}
                                                                    readOnly
                                                                />
                                                            </td>
                                                            <td className="border px-2 py-1">
                                                                <InputField
                                                                    value={product.cgst}
                                                                    name={`products[${index}].cgst`}
                                                                    type="number"
                                                                    style={{ width: "100%", minWidth: "6rem", maxWidth: "7rem", marginTop: '1rem' }}
                                                                    readOnly
                                                                />
                                                            </td>
                                                            <td className="border px-2 py-1">
                                                                <InputField
                                                                    value={product.sgst}
                                                                    name={`products[${index}].sgst`}
                                                                    type="number"
                                                                    style={{ width: "100%", minWidth: "6rem", maxWidth: "7rem", marginTop: '1rem' }}
                                                                    readOnly
                                                                />
                                                            </td>
                                                            <td className="border px-2 py-1">
                                                                <InputField
                                                                    value={product.igst}
                                                                    name={`products[${index}].igst`}
                                                                    type="number"
                                                                    style={{ width: "100%", minWidth: "6rem", maxWidth: "7rem", marginTop: '1rem' }}
                                                                    readOnly
                                                                />
                                                            </td>
                                                            <td className="border px-2 py-1">
                                                                <InputField
                                                                    value={product.totalTax}
                                                                    name={`products[${index}].totalTax`}
                                                                    type="number"
                                                                    style={{ width: "100%", minWidth: "6rem", maxWidth: "7rem", marginTop: '1rem' }}
                                                                    readOnly
                                                                />
                                                            </td>
                                                        </>
                                                    }
                                                    <td className="border px-2 py-1">
                                                        <InputField
                                                            value={product.totalUnitPrice}
                                                            name={`products[${index}].totalUnitPrice`}
                                                            type="number"
                                                            style={{ width: "100%", minWidth: "6rem", maxWidth: "7rem", marginTop: '1rem' }}
                                                            readOnly
                                                        />
                                                    </td>
                                                    <td className="border px-2 py-1">
                                                        <InputField
                                                            value={product.totalPrice}
                                                            name={`products[${index}].totalPrice`}
                                                            type="number"
                                                            style={{ width: "100%", minWidth: "6rem", maxWidth: "7rem", marginTop: '1rem' }}
                                                            readOnly
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>

                                    </table>
                                </div>
                            </div>

                            {/* Summary Section */}
                            <div className="bg-white p-4 mt-4 shadow-2xl">
                                <div className="text-lg font-bold mb-4 text-start">Summary</div>
                                <div className="flex gap-16 text-sm">
                                    <div>
                                        <div className="flex gap-4">
                                            <span>Total CGST:</span>
                                            <span>₹ {invoiceData.totalCgst}</span>
                                        </div>
                                        <div className="flex gap-4">
                                            <span>Total SGST:</span>
                                            <span>₹ {invoiceData.totalSgst}</span>
                                        </div>
                                        <div className="flex gap-4">
                                            <span>Total IGST:</span>
                                            <span>₹ {invoiceData.totalIgst}</span>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex gap-4">
                                            <span>Total Tax:</span>
                                            <span>₹ {parseFloat(invoiceData.totalCgst) + parseFloat(invoiceData.totalSgst) + parseFloat(invoiceData.totalIgst)}</span>
                                        </div>
                                        <div className="flex gap-4">
                                            <span>Subtotal:</span>
                                            <span>₹ {invoiceData.subTotal}</span>
                                        </div>
                                        <div className="flex gap-4 font-bold">
                                            <span>Grand Total:</span>
                                            <span>₹ {invoiceData.grandTotal}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex justify-end mt-4">
                                    <button
                                        type="button"
                                        className="bg-gray-500 text-white px-4 py-2 rounded mr-2"
                                        onClick={() => navigate(-1)}
                                    >
                                        Back
                                    </button>
                                    {accountData?.printPreference === "A4" &&
                                        <>
                                            <button
                                                type="button"
                                                className='bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 mr-2'
                                                onClick={() => generateBillForPrint(invoiceData)}
                                            >
                                                Print
                                            </button>

                                            <button
                                                type="button"
                                                className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                                                onClick={() => generateBillForDownload(invoiceData)}
                                            >
                                                Download
                                            </button>
                                        </>

                                    }
                                    {accountData?.printPreference === "landscape" &&
                                        <>
                                            <InvoicePrinter
                                                accountData={accountData}
                                                invoiceData={invoiceData}
                                                isMobile={isMobile}
                                            />

                                            <InvoiceDownload
                                                accountData={accountData}
                                                invoiceData={invoiceData}
                                            />
                                        </>
                                    }
                                    {accountData?.printPreference === "thermal" &&
                                        <ThermalInvoice
                                            invoiceData={invoiceData}
                                            accountData={accountData}
                                        />
                                    }
                                </div>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
        </InnerLayout>
    );
};

export default InvoiceView;